/**
 * Admin — Questionnaire Responses
 * Route: /admin/questionnaire-responses
 *
 * Displays submitted financial questionnaires with:
 *   - KPI cards: total, breathing/tight/heavy/needs_review counts, consultation rate
 *   - Filters: campaign, result_tier
 *   - Table: lead, ratio (tier chip), income, payments, equity, consultation flag, submitted_at
 *   - Row click → detail dialog with all raw answers + link to open Lead page
 *
 * Uses the existing endpoint:
 *   GET /api/admin/questionnaire/responses?campaign_id=&result_tier=&limit=
 */
import { useEffect, useMemo, useState } from "react";
import {
  Search,
  Filter,
  Phone,
  Mail,
  ExternalLink,
  Home as HomeIcon,
  Percent,
  Coins,
  Wallet,
  PhoneCall,
  ClipboardList,
} from "lucide-react";
import { toast } from "sonner";

import AdminLayout from "@/components/app/AdminLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { formatInAppTz } from "@/lib/timezone";

/* -------------------------------------------------------------------------- */
/* Tier metadata                                                              */
/* -------------------------------------------------------------------------- */
const TIER_META = {
  breathing: { label: "מרחב נשימה", chip: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  tight: { label: "מתחיל להיות צפוף", chip: "bg-amber-50 text-amber-800 border-amber-200" },
  heavy: { label: "מכביד על השוטף", chip: "bg-orange-50 text-orange-800 border-orange-200" },
  needs_review: { label: "דרושה בדיקה אישית", chip: "bg-rose-50 text-rose-800 border-rose-200" },
};

const CREDIT_HISTORY_LABEL = {
  clear: "הכול שולם כסדרו",
  isolated_cases: "מקרים נקודתיים",
  complex: "מצב מורכב יותר",
};

const CURRENCY = (n) => (typeof n === "number" ? n.toLocaleString("he-IL") + " ₪" : "—");
const PERCENT = (n) => (typeof n === "number" ? `${Math.round(n * 10) / 10}%` : "—");

/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */
export default function AdminQuestionnaireResponses() {
  const [items, setItems] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [campaignId, setCampaignId] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null); // response detail dialog

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (campaignId) params.set("campaign_id", campaignId);
      if (tierFilter) params.set("result_tier", tierFilter);
      params.set("limit", "500");
      const { data } = await api.get(`/admin/questionnaire/responses?${params.toString()}`);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "שגיאה בטעינת התוצאות");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/campaigns");
        setCampaigns(Array.isArray(data) ? data : []);
      } catch (_) { /* silent */ }
    })();
  }, []);

  useEffect(() => { load(); }, [campaignId, tierFilter]);

  /* Client-side search over lead name/email/phone */
  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter((r) =>
      [r.lead_name, r.lead_email, r.lead_phone]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [items, search]);

  /* KPI cards */
  const kpis = useMemo(() => {
    const total = filtered.length;
    const tiers = { breathing: 0, tight: 0, heavy: 0, needs_review: 0 };
    let consult = 0;
    for (const r of filtered) {
      if (r.result_tier && tiers[r.result_tier] !== undefined) tiers[r.result_tier] += 1;
      if (r.consultation_requested) consult += 1;
    }
    const conversionPct = total > 0 ? Math.round((consult / total) * 100) : 0;
    return { total, tiers, consult, conversionPct };
  }, [filtered]);

  return (
    <AdminLayout title="תוצאות שאלון">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5" data-testid="q-kpi-row">
        <KpiCard label="סה״כ הגשות" value={kpis.total} testId="kpi-total" />
        <KpiCard
          label="ביקשו שיחת ייעוץ"
          value={`${kpis.consult} (${kpis.conversionPct}%)`}
          testId="kpi-consult"
          accent
        />
        <TierKpi label="מרחב נשימה" value={kpis.tiers.breathing} tier="breathing" />
        <TierKpi label="מתחיל להיות צפוף" value={kpis.tiers.tight} tier="tight" />
        <TierKpi label="מכביד על השוטף" value={kpis.tiers.heavy} tier="heavy" />
        <TierKpi label="דרושה בדיקה" value={kpis.tiers.needs_review} tier="needs_review" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 border border-stone-200 mb-5">
        <div className="grid md:grid-cols-12 gap-3 items-center">
          <div className="md:col-span-4 relative">
            <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="חיפוש שם / אימייל / טלפון"
              className="h-11 pr-9"
              data-testid="q-search-input"
            />
          </div>
          <div className="md:col-span-4">
            <Select value={campaignId || "ALL"} onValueChange={(v) => setCampaignId(v === "ALL" ? "" : v)}>
              <SelectTrigger className="h-11" data-testid="q-filter-campaign">
                <SelectValue placeholder="כל הקמפיינים" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">כל הקמפיינים</SelectItem>
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.brand_name} (/{c.slug})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3">
            <Select value={tierFilter || "ALL"} onValueChange={(v) => setTierFilter(v === "ALL" ? "" : v)}>
              <SelectTrigger className="h-11" data-testid="q-filter-tier">
                <SelectValue placeholder="כל התוצאות" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">כל התוצאות</SelectItem>
                {Object.entries(TIER_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-1">
            <Button
              onClick={load}
              className="w-full h-11 bg-brand-charcoal text-white rounded-xl"
              data-testid="q-refresh-btn"
            >
              <Filter className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="q-responses-table">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr className="text-right">
                <Th>ליד</Th>
                <Th>תוצאה</Th>
                <Th>יחס החזר</Th>
                <Th>הכנסה נטו</Th>
                <Th>סה״כ החזרים</Th>
                <Th>הון בנכס</Th>
                <Th>שיחת ייעוץ</Th>
                <Th>נשלח</Th>
                <Th className="w-16"></Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={9} className="px-4 py-16 text-center text-stone-400">טוען…</td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-16 text-center text-stone-400">
                    <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    אין תוצאות שאלון תואמות לסינון הנוכחי
                  </td>
                </tr>
              )}
              {!loading && filtered.map((r) => (
                <ResponseRow key={r.id} row={r} onClick={() => setSelected(r)} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
          {selected && <ResponseDetail response={selected} />}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

/* -------------------------------------------------------------------------- */
/* Sub components                                                             */
/* -------------------------------------------------------------------------- */
function Th({ children, className = "" }) {
  return (
    <th className={`px-3 py-3 text-xs font-bold text-stone-600 uppercase tracking-wider ${className}`}>
      {children}
    </th>
  );
}

function KpiCard({ label, value, testId, accent = false }) {
  return (
    <div
      className={`rounded-2xl p-4 border ${accent ? "bg-amber-50 border-amber-200" : "bg-white border-stone-200"}`}
      data-testid={testId}
    >
      <div className="text-xs font-semibold text-stone-500">{label}</div>
      <div className={`mt-1 text-2xl font-black ${accent ? "text-brand-amber" : "text-brand-charcoal"}`}>
        {value}
      </div>
    </div>
  );
}

function TierKpi({ label, value, tier }) {
  const meta = TIER_META[tier];
  return (
    <div className={`rounded-2xl p-4 border ${meta.chip}`} data-testid={`kpi-tier-${tier}`}>
      <div className="text-xs font-semibold opacity-80">{label}</div>
      <div className="mt-1 text-2xl font-black">{value}</div>
    </div>
  );
}

function ResponseRow({ row, onClick }) {
  const meta = TIER_META[row.result_tier] || TIER_META.tight;
  return (
    <tr
      className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer transition-colors"
      onClick={onClick}
      data-testid={`q-row-${row.id}`}
    >
      <td className="px-3 py-3">
        <div className="font-semibold text-brand-charcoal">{row.lead_name || "—"}</div>
        <div className="text-xs text-stone-500 flex flex-wrap gap-x-3">
          {row.lead_email && <span className="ltr">{row.lead_email}</span>}
          {row.lead_phone && <span className="ltr">{row.lead_phone}</span>}
        </div>
      </td>
      <td className="px-3 py-3">
        <Badge className={`${meta.chip} border font-semibold`}>{meta.label}</Badge>
      </td>
      <td className="px-3 py-3 font-bold text-brand-charcoal">{PERCENT(row.payment_to_income_ratio)}</td>
      <td className="px-3 py-3">{CURRENCY(row.answers?.monthly_net_income)}</td>
      <td className="px-3 py-3">{CURRENCY(row.total_monthly_payments)}</td>
      <td className="px-3 py-3">
        {row.estimated_property_equity === null || row.estimated_property_equity === undefined
          ? <span className="text-stone-400">—</span>
          : CURRENCY(row.estimated_property_equity)}
      </td>
      <td className="px-3 py-3">
        {row.consultation_requested ? (
          <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 font-semibold">
            <PhoneCall className="w-3 h-3 ms-1" />
            ביקש/ה
          </Badge>
        ) : (
          <span className="text-xs text-stone-400">—</span>
        )}
      </td>
      <td className="px-3 py-3 text-xs text-stone-500 whitespace-nowrap">
        {formatInAppTz(row.created_at, { format: "DD/MM/YYYY HH:mm" })}
      </td>
      <td className="px-3 py-3 text-center">
        <ExternalLink className="w-4 h-4 text-stone-400" />
      </td>
    </tr>
  );
}

function ResponseDetail({ response }) {
  const meta = TIER_META[response.result_tier] || TIER_META.tight;
  const a = response.answers || {};
  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-right">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xl font-black">{response.lead_name || "ליד ללא שם"}</span>
            <Badge className={`${meta.chip} border font-semibold`}>{meta.label}</Badge>
            {response.consultation_requested && (
              <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 font-semibold">
                <PhoneCall className="w-3 h-3 ms-1" />
                ביקש/ה שיחת ייעוץ
              </Badge>
            )}
          </div>
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-4">
        {/* Contact */}
        <div className="flex gap-4 flex-wrap text-sm">
          {response.lead_email && (
            <a href={`mailto:${response.lead_email}`} className="inline-flex items-center gap-1.5 text-brand-amber hover:underline">
              <Mail className="w-4 h-4" /> <span className="ltr">{response.lead_email}</span>
            </a>
          )}
          {response.lead_phone && (
            <a href={`tel:${response.lead_phone}`} className="inline-flex items-center gap-1.5 text-brand-amber hover:underline">
              <Phone className="w-4 h-4" /> <span className="ltr">{response.lead_phone}</span>
            </a>
          )}
        </div>

        {/* Computed KPIs */}
        <div className="grid grid-cols-3 gap-3">
          <MiniStat
            icon={Percent}
            label="יחס החזר"
            value={PERCENT(response.payment_to_income_ratio)}
            highlight
          />
          <MiniStat
            icon={Wallet}
            label="הכנסה נטו"
            value={CURRENCY(a.monthly_net_income)}
          />
          <MiniStat
            icon={Coins}
            label="סה״כ החזרים"
            value={CURRENCY(response.total_monthly_payments)}
          />
        </div>

        {/* Raw answers */}
        <div className="rounded-2xl border border-stone-200 divide-y divide-stone-100">
          <AnswerRow icon={HomeIcon} label="בעלות על נכס" value={a.has_property ? "כן" : "לא"} />
          {a.has_property && <AnswerRow label="שווי הנכס" value={CURRENCY(a.property_value)} />}
          {a.has_property && <AnswerRow label="יתרת המשכנתא" value={CURRENCY(a.mortgage_balance)} />}
          {a.has_property && <AnswerRow label="החזר משכנתא חודשי" value={CURRENCY(a.monthly_mortgage_payment)} />}
          <AnswerRow label="החזרי הלוואות אחרות" value={CURRENCY(a.monthly_loan_payments)} />
          <AnswerRow
            label="היסטוריית אשראי"
            value={CREDIT_HISTORY_LABEL[a.credit_history] || a.credit_history || "—"}
          />
          {response.estimated_property_equity !== null && response.estimated_property_equity !== undefined && (
            <AnswerRow
              label="הון משוער בנכס (שווי − יתרה)"
              value={CURRENCY(response.estimated_property_equity)}
              subtle
            />
          )}
        </div>

        {/* Meta */}
        <div className="text-xs text-stone-500 space-y-1 pt-2">
          <div><strong>נשלח:</strong> {formatInAppTz(response.created_at, { format: "DD/MM/YYYY HH:mm:ss" })}</div>
          {response.consultation_requested_at && (
            <div><strong>בקשת ייעוץ:</strong> {formatInAppTz(response.consultation_requested_at, { format: "DD/MM/YYYY HH:mm:ss" })}</div>
          )}
          <div><strong>Response ID:</strong> <span className="ltr">{response.id}</span></div>
          <div><strong>Lead ID:</strong> <span className="ltr">{response.lead_id}</span></div>
          {(response.lead_tags || []).length > 0 && (
            <div>
              <strong>תגי ליד:</strong>{" "}
              {response.lead_tags.map((t) => (
                <Badge key={t} className="ms-1 bg-stone-100 text-stone-700 border-stone-200 border font-normal">{t}</Badge>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function AnswerRow({ icon: Icon, label, value, subtle = false }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5 text-sm">
      <div className={`flex items-center gap-2 ${subtle ? "text-stone-500" : "text-stone-700"}`}>
        {Icon && <Icon className="w-4 h-4" />}
        <span>{label}</span>
      </div>
      <div className={`font-bold ${subtle ? "text-stone-500" : "text-brand-charcoal"}`}>{value}</div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, highlight = false }) {
  return (
    <div
      className={`rounded-xl p-3 border ${highlight ? "bg-amber-50 border-amber-200" : "bg-stone-50 border-stone-200"}`}
    >
      <div className={`flex items-center gap-1.5 text-xs font-semibold ${highlight ? "text-brand-amber" : "text-stone-500"}`}>
        <Icon className="w-3.5 h-3.5" />
        <span>{label}</span>
      </div>
      <div className={`mt-1 text-lg font-black ${highlight ? "text-brand-amber" : "text-brand-charcoal"}`}>{value}</div>
    </div>
  );
}
